import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useUserContext } from "../../context/useUserContext";
import { useOutletContext } from "react-router-dom";
import { showGenericError } from "../../helper/Notifications";
import CustomModal from "../../Components/Generic/CustomModal";
import { Input } from "../../shadcn/input";

const TwoFAModal = ({ setIsModalOpen }) => {
  const { enable2FA, getOTPQrcode, handleSetUser, user } = useUserContext();
  const { setLoading } = useOutletContext();

  const [qrCodeSvg, setQrCodeSvg] = useState(null);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  const otpValue = useMemo(() => otpDigits.join(""), [otpDigits]);

  const handleEnable2FA = (enable, otpCode) => {
    setLoading(true);
    enable2FA(
      enable,
      otpCode,
      (response) => {
        setOtpDigits(["", "", "", "", "", ""]);
        handleSetUser(response.data.user);
        setLoading(false);
        setIsModalOpen(false);
      },
      () => {
        setOtpDigits(["", "", "", "", "", ""]);
        showGenericError();
        setLoading(false);
      }
    );
  };

  const handleGetQrCode = () => {
    getOTPQrcode(
      (response) => setQrCodeSvg(response.data),
      (error) => console.log(error)
    );
  };

  const handleConfirm = () => {
    if (otpValue.length !== 6) return;
    if (user?.otp_required_for_login) {
      handleEnable2FA(false, otpValue);
    } else {
      handleEnable2FA(true, otpValue);
    }
  };

  // OTP segmented input behavior
  const handleChangeDigit = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 0) {
      const next = [...otpDigits];
      next[index] = "";
      setOtpDigits(next);
      return;
    }
    if (cleaned.length > 1) {
      // paste case
      const next = [...otpDigits];
      let i = index;
      for (const ch of cleaned.slice(0, 6 - index)) {
        next[i] = ch;
        i++;
      }
      setOtpDigits(next);
      const focusIndex = Math.min(index + cleaned.length - 1, 5);
      inputsRef.current[focusIndex]?.focus();
      return;
    }
    const next = [...otpDigits];
    next[index] = cleaned;
    setOtpDigits(next);
    if (cleaned && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
    if (e.key === "Enter") {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (!user?.otp_required_for_login) handleGetQrCode();
  }, [user]);

  const title = user?.otp_required_for_login
    ? "Disable two-factor authentication"
    : "Set up two-factor authentication";

  const description = user?.otp_required_for_login
    ? "Enter a code from your authenticator to confirm."
    : "Keep your account safer by turning on 2FA. You'll sign in with your password and a one-time code from your phone.";

  return (
    <CustomModal
      isOpen={true}
      onOpenChange={(open) => !open && setIsModalOpen(false)}
      iconType="warningTriangle"
      iconAlignment="left"
      title={title}
      titleAlignment="left"
      // Design-specific container styling
      containerWidth={512}
      containerPadding={24}
      containerRadius={12}
      containerGap={32}
      containerShadow="doubleXL"
      buttons={[
        { text: "Cancel", variant: "secondary", onClick: () => setIsModalOpen(false) },
        {
          text: user?.otp_required_for_login ? "Disable" : "Confirm",
          variant: "primary",
          onClick: handleConfirm,
          disabled: otpValue.length < 6,
        },
      ]}
    >
      <div className="flex flex-col gap-4 text-left">
        <p className="text-gray-600">{description}</p>

        {!user?.otp_required_for_login && (
          <QRWrapper>
            {qrCodeSvg ? (
              <div className="qr" dangerouslySetInnerHTML={{ __html: qrCodeSvg }} />
            ) : (
              <div className="placeholder" />
            )}
          </QRWrapper>
        )}

        <div className="w-full">
          <p className="font-inter text-sm font-medium leading-5 text-gray-700 mb-[6px]">Verification code</p>
          <div className="flex items-center gap-2.5 flex-nowrap">
            {otpDigits.map((d, i) => (
              <React.Fragment key={i}>
                {i === 3 && (
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center h-16 text-gray-300 text-[60px] leading-[72px] font-medium tracking-[-0.02em]"
                  >
                    -
                  </span>
                )}
                <Input
                  ref={(el) => (inputsRef.current[i] = el)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChangeDigit(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="h-16 w-16 rounded-lg text-center text-[24px] font-semibold tracking-[6px]"
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default TwoFAModal;


const QRWrapper = styled.div`
  background: #f9fafb;
  border: 1px solid #f2f4f7;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  .qr svg {
    display: block;
    width: 140px;
    height: 140px;
  }
  .placeholder {
    width: 140px;
    height: 140px;
    background: #e5e7eb;
    border-radius: 8px;
  }
`;


// OTP row now uses Tailwind classes inline

// Dash now styled inline with Tailwind classes near its usage
